<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12" lg="5">
        <b-card>
          <template #header>
            <h5 class="m-0">Nova declaracao</h5>
          </template>
          <b-form @submit.prevent="submitDeclaration">
            <b-form-group label="Tipo" label-for="gh-type">
              <b-form-select
                id="gh-type"
                v-model="form.type"
                :options="typeOptions"
                required
              />
            </b-form-group>

            <b-form-group label="Valor" label-for="gh-value">
              <b-form-input
                id="gh-value"
                v-model.number="form.value"
                type="number"
                min="0"
                step="0.01"
                required
              />
            </b-form-group>

            <b-form-group label="Quem recebeu" label-for="gh-recipient">
              <b-form-input
                id="gh-recipient"
                v-model="form.recipientName"
                required
              />
            </b-form-group>

            <b-form-group label="Empresa que enviou" label-for="gh-sender">
              <b-form-input
                id="gh-sender"
                v-model="form.senderCompany"
                required
              />
            </b-form-group>

            <b-form-group label="Data" label-for="gh-offered-at">
              <b-form-input
                id="gh-offered-at"
                v-model="form.offeredAt"
                type="date"
                required
              />
            </b-form-group>

            <b-form-group
              label="Centro de custo/Area"
              label-for="gh-cost-center"
            >
              <b-form-input
                id="gh-cost-center"
                v-model="form.costCenter"
                required
              />
            </b-form-group>

            <b-form-group label="Justificativa/Contexto" label-for="gh-context">
              <b-form-textarea
                id="gh-context"
                v-model="form.context"
                rows="4"
                required
              />
            </b-form-group>

            <b-form-group label="Anexo">
              <b-form-file
                v-model="form.file"
                browse-text="Selecionar"
                required
              />
            </b-form-group>

            <b-button type="submit" variant="primary" :disabled="submitting">
              <b-spinner v-if="submitting" small class="mr-1" />
              Enviar declaracao
            </b-button>
          </b-form>
        </b-card>
      </b-col>

      <b-col cols="12" lg="7">
        <b-card>
          <template #header>
            <h5 class="m-0">Minhas declaracoes</h5>
          </template>
          <b-card-body>
            <div class="d-flex align-items-end mb-3">
              <b-form-group class="w-100 mb-0" label="Pesquisar protocolo">
                <b-input-group>
                  <b-form-input
                    v-model="filterProtocol"
                    @keyup.enter="applyFilter"
                  />
                  <b-input-group-append>
                    <b-button :disabled="!filterProtocol" @click="clearFilter"
                      >Limpar</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </div>

            <b-table
              :items="declarations.docs"
              :fields="fields"
              :busy="loading"
              no-local-sorting
              show-empty
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
            >
              <template #cell(status)="slotData">
                <b-badge :variant="statusVariant(slotData.item.status)">
                  {{ statusLabel(slotData.item.status) }}
                </b-badge>
              </template>
              <template #cell(type)="slotData">
                {{ typeLabel(slotData.item.type) }}
              </template>
              <template #cell(value)="slotData">
                {{ formatMoney(slotData.item.value) }}
              </template>
              <template #cell(offeredAt)="slotData">
                {{ formatDate(slotData.item.offeredAt) }}
              </template>
              <template #cell(createdAt)="slotData">
                {{ formatDate(slotData.item.createdAt) }}
              </template>
              <template #empty>
                <div class="text-muted text-center py-4">
                  Nenhuma declaracao encontrada.
                </div>
              </template>
            </b-table>

            <b-pagination
              v-model="declarations.page"
              :total-rows="declarations.total"
              :per-page="declarations.limit"
              prev-text="Anterior"
              next-text="Proximo"
              align="center"
              class="mt-3"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  createGiftsHospitalityDeclaration,
  getMyGiftsHospitalityDeclarations,
} from "../../api/gifts-hospitality";

export default {
  name: "GiftsHospitalityUser",
  data() {
    return {
      loading: false,
      submitting: false,
      sortBy: "createdAt",
      sortDesc: true,
      filterProtocol: "",
      form: {
        type: "",
        value: null,
        recipientName: "",
        senderCompany: "",
        offeredAt: "",
        context: "",
        costCenter: "",
        file: null,
      },
      typeOptions: [
        { value: "", text: "Selecione" },
        { value: "gift", text: "Brinde" },
        { value: "hospitality", text: "Hospitalidade" },
      ],
      fields: [
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "type", label: "Tipo", sortable: true },
        { key: "value", label: "Valor", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "offeredAt", label: "Data", sortable: true },
        { key: "createdAt", label: "Criado em", sortable: true },
      ],
      declarations: {
        docs: [],
        page: 1,
        limit: 15,
        total: 0,
      },
    };
  },
  watch: {
    "declarations.page"() {
      this.fetchDeclarations();
    },
  },
  mounted() {
    this.fetchDeclarations();
  },
  methods: {
    typeLabel(type) {
      return type === "hospitality" ? "Hospitalidade" : "Brinde";
    },
    statusLabel(status) {
      const labels = {
        submitted: "Enviado",
        approved: "Aprovado",
        rejected: "Reprovado",
      };
      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        submitted: "warning",
        approved: "success",
        rejected: "danger",
      };
      return variants[status] || "secondary";
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("pt-BR");
    },
    formatMoney(value) {
      if (!Number.isFinite(value)) return "-";
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      }
      this.fetchDeclarations();
    },
    applyFilter() {
      this.declarations.page = 1;
      this.fetchDeclarations();
    },
    clearFilter() {
      this.filterProtocol = "";
      this.applyFilter();
    },
    async fetchDeclarations() {
      this.loading = true;
      try {
        const response = await getMyGiftsHospitalityDeclarations({
          page: this.declarations.page,
          size: this.declarations.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          protocol: this.filterProtocol || undefined,
        });
        this.declarations = {
          ...this.declarations,
          ...response.data,
        };
      } catch (error) {
        this.$bvToast.toast("Erro ao carregar declaracoes.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.loading = false;
      }
    },
    async submitDeclaration() {
      this.submitting = true;
      try {
        const payload = new FormData();
        payload.append("type", this.form.type);
        payload.append("value", this.form.value);
        payload.append("recipientName", this.form.recipientName);
        payload.append("senderCompany", this.form.senderCompany);
        payload.append("offeredAt", this.form.offeredAt);
        payload.append("context", this.form.context);
        payload.append("costCenter", this.form.costCenter);
        payload.append("file", this.form.file);

        const response = await createGiftsHospitalityDeclaration(payload);

        this.$bvToast.toast(
          `Declaracao enviada. Protocolo: ${response.data.protocol}`,
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );

        this.form = {
          type: "",
          value: null,
          recipientName: "",
          senderCompany: "",
          offeredAt: "",
          context: "",
          costCenter: "",
          file: null,
        };

        await this.fetchDeclarations();
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Nao foi possivel enviar a declaracao.";
        this.$bvToast.toast(message, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
